import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { differenceInDays } from 'date-fns';

import { hideLoader, showLoader } from '@topconagriculture/loader';

import {
  addIngredientPrice,
  createConcentrate,
  createMilking,
  createMilkPrice,
  deleteLoadDeliveryBatch,
  deleteMilkPrice,
  getConcentrates,
  getIngredientPrices,
  getLoadsDeliveries,
  getLoadsDeliveriesBatches,
  getMilkings,
  getMilkPrices,
  sendLoadsData,
  updateConcentrate,
  updateDelivery,
  updateIngredientPrice,
  updateLoad,
  updateMilking,
  updateMilkPrice,
} from './dataTools.actions';
import { LoadersNames } from '../../ui/ui.constants';
import api from '../../../core/api';
import { notificationError, notificationSuccess } from '../../../core/helpers/utils/notificationUtils';
import { composeErrorMessage } from '../../../core/helpers/utils/utils';
import { settingsSelector } from '../../selectors';
import { toLocalDate, toTimezoneDate } from '../../../core/helpers/utils/dateUtils';
import { compose403ErrorMessage } from '../../../core/helpers/utils/sagaUtils';

function* getLoadsDeliveriesDataBatchesSaga({ payload }: ReturnType<typeof getLoadsDeliveriesBatches.started>) {
  yield put(showLoader(LoadersNames.fetchLoadsDeliveriesBatches));
  try {
    const { startDate, endDate } = payload;
    const timezone = yield select(settingsSelector.getTimeZone);
    const daysBack = differenceInDays(endDate, startDate);
    const result = yield call(
      api.dataTools.getLoadsDeliveriesBatches,
      daysBack,
      toTimezoneDate(toLocalDate(endDate), timezone),
    );
    yield put(
      getLoadsDeliveriesBatches.done({
        params: payload,
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchLoadsDeliveriesBatches));
}

function* getLoadsDeliveriesSaga({ payload }: ReturnType<typeof getLoadsDeliveries.started>) {
  yield put(showLoader(LoadersNames.fetchLoadsDeliveries));
  try {
    const result = yield all(payload.map((id) => call(api.dataTools.getLoadsDeliveriesData, id)));
    yield put(
      getLoadsDeliveries.done({
        params: payload,
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchLoadsDeliveries));
}

function* updateLoadSaga(action: ReturnType<typeof updateLoad.started>) {
  yield put(showLoader(LoadersNames.updateLoadsDeliveries));
  try {
    const { batchId, loadId, data, ignoreStock, formName } = action.payload;
    const result = yield call(api.dataTools.updateLoad, batchId, loadId, data, !ignoreStock, formName);

    yield put(
      updateLoad.done({
        params: action.payload,
        result,
      }),
    );
    yield put(notificationSuccess('dataTools:updated_load_success'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.updateLoadsDeliveries));
}

function* updateDeliverySaga(action: ReturnType<typeof updateDelivery.started>) {
  yield put(showLoader(LoadersNames.updateLoadsDeliveries));
  try {
    const { batchId, deliveryId, data, ignoreStock, formName } = action.payload;
    const { refusalPerc, ...deliveryData } = data;
    const result = yield call(api.dataTools.updateDelivery, batchId, deliveryId, deliveryData, !ignoreStock, formName);

    yield put(
      updateDelivery.done({
        params: action.payload,
        result,
      }),
    );
    yield put(notificationSuccess('dataTools:updated_delivery_success'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.updateLoadsDeliveries));
}

function* deleteLoadDeliveryBatchSaga(action: ReturnType<typeof deleteLoadDeliveryBatch.started>) {
  yield put(showLoader(LoadersNames.deleteLoadDeliveryBatch));
  try {
    const { id, formName, ignoreStock } = action.payload;
    const result = yield call(api.dataTools.deleteLoadDeliveryBatch, id, formName, !ignoreStock);

    yield put(
      deleteLoadDeliveryBatch.done({
        params: action.payload,
        result,
      }),
    );
    yield put(notificationSuccess('dataTools:deleted_batch_success'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.deleteLoadDeliveryBatch));
}

function* getIngredientPricesSaga({ payload }: ReturnType<typeof getIngredientPrices.started>) {
  yield put(showLoader(LoadersNames.fetchIngredientsPrices));
  try {
    const result = yield call(api.dataTools.getIngredientPrices, payload);

    yield put(
      getIngredientPrices.done({
        params: payload,
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchIngredientsPrices));
}

function* updateIngredientPriceSaga(action: ReturnType<typeof updateIngredientPrice.started>) {
  yield put(showLoader(LoadersNames.updateIngredientPrice));
  try {
    const { ingredientId, priceId, data, formName } = action.payload;
    const result = yield call(api.dataTools.updateIngredientPrice, ingredientId, priceId, data, formName);

    yield put(
      updateIngredientPrice.done({
        params: action.payload,
        result,
      }),
    );

    const meta = action.meta;
    if (meta && typeof meta.onSuccess) {
      meta.onSuccess();
    }
    yield put(notificationSuccess('dataTools:updated_price_success'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.updateIngredientPrice));
}

function* addIngredientPriceSaga(action: ReturnType<typeof addIngredientPrice.started>) {
  yield put(showLoader(LoadersNames.addIngredientPrice));
  try {
    const { ingredientId, data, formName } = action.payload;
    const result = yield call(api.dataTools.addIngredientPrice, ingredientId, data, formName);

    yield put(
      addIngredientPrice.done({
        params: action.payload,
        result,
      }),
    );

    const meta = action.meta;
    if (meta && typeof meta.onSuccess) {
      meta.onSuccess();
    }
    yield put(notificationSuccess('dataTools:added_price_success'));
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.addIngredientPrice));
}

function* getMilkingsSaga(action: ReturnType<typeof getMilkings.started>) {
  const silent = action.meta && action.meta.silent;

  if (!silent) {
    yield put(showLoader(LoadersNames.fetchMilkings));
  }
  try {
    const {
      payload: { startDate, endDate },
    } = action;
    const timezone = yield select(settingsSelector.getTimeZone);
    const daysBack = differenceInDays(endDate, startDate);
    const result = yield call(api.dataTools.getMilkings, daysBack, toTimezoneDate(toLocalDate(endDate), timezone));
    yield put(
      getMilkings.done({
        params: { startDate, endDate },
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  if (!silent) {
    yield put(hideLoader(LoadersNames.fetchMilkings));
  }
}

function* createMilkingSaga(action: ReturnType<typeof createMilking.started>) {
  try {
    const { data, formName } = action.payload;
    const result = yield call(api.dataTools.createMilking, data, formName);
    yield put(
      createMilking.done({
        params: action.payload,
        result,
      }),
    );
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
}

function* updateMilkingsSaga(action: ReturnType<typeof updateMilking.started>) {
  try {
    const { data, formName } = action.payload;
    const { id } = data;
    const result = yield call(api.dataTools.updateMilking, id as string, data, formName);
    yield put(
      updateMilking.done({
        params: action.payload,
        result,
      }),
    );
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
}

function* getConcentratesSaga(action: ReturnType<typeof getConcentrates.started>) {
  yield put(showLoader(LoadersNames.fetchConcentrates));
  try {
    const {
      payload: { startDate, endDate },
    } = action;
    const timezone = yield select(settingsSelector.getTimeZone);
    const daysBack = differenceInDays(endDate, startDate);
    const result = yield call(api.dataTools.getConcentrates, daysBack, toTimezoneDate(toLocalDate(endDate), timezone));
    yield put(
      getConcentrates.done({
        params: { startDate, endDate },
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchConcentrates));
}

function* createConcentrateSaga(action: ReturnType<typeof createConcentrate.started>) {
  try {
    const { data, formName } = action.payload;
    const result = yield call(api.dataTools.createConcentrate, data, formName);
    yield put(
      createConcentrate.done({
        params: action.payload,
        result,
      }),
    );
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
}

function* updateConcentrateSaga(action: ReturnType<typeof updateConcentrate.started>) {
  try {
    const { data, formName } = action.payload;
    const { id } = data;
    const result = yield call(api.dataTools.updateConcentrate, id as string, data, formName);
    yield put(
      updateConcentrate.done({
        params: action.payload,
        result,
      }),
    );
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
}

function* getMilkPricesSaga(action: ReturnType<typeof getMilkPrices.started>) {
  yield put(showLoader(LoadersNames.fetchMilkPrices));
  const { payload } = action;
  try {
    const result = yield call(api.dataTools.getMilkPrices);
    yield put(
      getMilkPrices.done({
        params: payload,
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchMilkPrices));
}

function* createMilkPriceSaga(action: ReturnType<typeof createMilkPrice.started>) {
  yield put(showLoader(LoadersNames.createMilkPrice));
  try {
    const { data, formName } = action.payload;
    const result = yield call(api.dataTools.createMilkPrice, data, formName);
    yield put(
      createMilkPrice.done({
        params: action.payload,
        result,
      }),
    );
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.createMilkPrice));
}

function* updateMilkPriceSaga(action: ReturnType<typeof updateMilkPrice.started>) {
  yield put(showLoader(LoadersNames.updateMilkPrice));
  try {
    const { data, formName } = action.payload;
    const { id } = data;
    const result = yield call(api.dataTools.updateMilkPrice, id as string, data, formName);
    yield put(
      updateMilkPrice.done({
        params: action.payload,
        result,
      }),
    );
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.updateMilkPrice));
}

function* deleteMilkPriceSaga(action: ReturnType<typeof deleteMilkPrice.started>) {
  yield put(showLoader(LoadersNames.deleteMilkPrice));
  try {
    const { id, formName } = action.payload;
    yield call(api.dataTools.deleteMilkPrice, id, formName);
    yield put(
      deleteMilkPrice.done({
        params: action.payload,
        result: id,
      }),
    );
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.deleteMilkPrice));
}

function* sendLoadsDataSaga(action: ReturnType<typeof sendLoadsData.started>) {
  try {
    yield put(showLoader(LoadersNames.sendLoadsdata));
    yield call(api.dataTools.sendLoadsData, action.payload);

    const meta = action.meta;
    if (meta && typeof meta.onSuccess) {
      meta.onSuccess();
    }

    yield put(notificationSuccess('dataTools:uploaded_files_success'));
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  } finally {
    yield put(hideLoader(LoadersNames.sendLoadsdata));
  }
}

export function* watchDataToolsSaga() {
  yield takeLatest(getLoadsDeliveriesBatches.started, getLoadsDeliveriesDataBatchesSaga);
  yield takeLatest(getLoadsDeliveries.started, getLoadsDeliveriesSaga);
  yield takeLatest(updateLoad.started, updateLoadSaga);
  yield takeLatest(updateDelivery.started, updateDeliverySaga);
  yield takeLatest(deleteLoadDeliveryBatch.started, deleteLoadDeliveryBatchSaga);
  yield takeLatest(getIngredientPrices.started, getIngredientPricesSaga);
  yield takeLatest(updateIngredientPrice.started, updateIngredientPriceSaga);
  yield takeLatest(addIngredientPrice.started, addIngredientPriceSaga);
  yield takeLatest(getMilkings.started, getMilkingsSaga);
  yield takeLatest(createMilking.started, createMilkingSaga);
  yield takeLatest(updateMilking.started, updateMilkingsSaga);
  yield takeLatest(getConcentrates.started, getConcentratesSaga);
  yield takeLatest(createConcentrate.started, createConcentrateSaga);
  yield takeLatest(updateConcentrate.started, updateConcentrateSaga);
  yield takeLatest(getMilkPrices.started, getMilkPricesSaga);
  yield takeLatest(createMilkPrice.started, createMilkPriceSaga);
  yield takeLatest(updateMilkPrice.started, updateMilkPriceSaga);
  yield takeLatest(deleteMilkPrice.started, deleteMilkPriceSaga);
  yield takeLatest(sendLoadsData.started, sendLoadsDataSaga);
}
